import React from 'react';
import { Link } from 'react-router-dom';
import './TermsOfService.scss';

const TermsOfService = () => {
  return (
    <div className="legal-page">
      <h1>Terms of Service</h1>
      <div className="legal-content">
        <p>Last updated: {new Date().toLocaleDateString()}</p>
        
        <h2>1. Acceptance of Terms</h2>
        <p>By accessing and using Audioread™ ("the Service"), operated by Audioread by Astrophe, LLC ("Company"), a Delaware limited liability company based in the United States, you agree to be bound by these Terms of Service. If you are accessing our Service from the European Economic Area ("EEA"), additional terms may apply as outlined in the EU-Specific Terms section.</p>

        <h2>2. Description of Service</h2>
        <p>Audioread provides text-to-speech conversion services for articles, PDFs, emails, and other text content.</p>

        <h2>3. User Accounts</h2>
        <p>You must create an account to use certain features of the Service. You are responsible for maintaining the confidentiality of your account information.</p>

        <h2>4. Communications and Email Consent</h2>
        <p>By creating an account with Audioread, you acknowledge and agree to our email communication practices. We are committed to protecting your privacy and managing your communication preferences transparently. You explicitly consent to receive:</p>
        <ul>
          <li>Essential service communications: Including but not limited to account verification, security notifications, and changes to our terms or policies</li>
          <li>Transactional emails: Related to your subscription, payments, and service usage</li>
          <li>Product updates and features: Information about new features, improvements, and service updates</li>
          <li>Marketing communications: Including promotional offers and newsletters (subject to your explicit consent)</li>
        </ul>
        <p>Your Rights and Choices:</p>
        <ul>
          <li>You have the right to withdraw your consent for marketing communications at any time</li>
          <li>All marketing emails include an unsubscribe link for easy opt-out</li>
          <li>You can manage your email preferences through your account settings at any time</li>
          <li>Opting out of marketing communications will not affect essential service communications necessary for the operation of your account</li>
          <li>We process your email data securely and in accordance with our Privacy Policy and applicable data protection laws</li>
          <li>You can contact us at any time to update your communication preferences</li>
        </ul>
        <p>We respect your privacy and are committed to providing you with control over your communication preferences. Essential service communications will continue to ensure the proper functioning of your account and service delivery.</p>

        <h2>5. Subscription and Payments</h2>
        <p>Subscription fees are charged on a recurring basis. While you may cancel your subscription at any time, all payments are final and non-refundable. Cancellation will stop future payments, but we are not required to refund any payments made before cancellation. The Company reserves the right to change subscription fees upon reasonable notice. For EU customers, additional consumer protection rights may apply under local law.</p>

        <h2>6. Acceptable Use</h2>
        <p>You agree not to misuse the Service or help anyone else do so. You must not:</p>
        <ul>
          <li>Use the Service for any illegal purpose</li>
          <li>Attempt to bypass usage limits</li>
          <li>Share your account credentials</li>
          <li>Reverse engineer the Service</li>
          <li>Upload content that infringes on intellectual property rights</li>
        </ul>

        <h2>7. Content Rights</h2>
        <p>You retain rights to your content. You grant Audioread by Astrophe, LLC a license to convert and process your content for the purpose of providing the Service.</p>

        <h2>8. Intellectual Property</h2>
        <p>The Service and its original content, features, and functionality are owned by Audioread by Astrophe, LLC and are protected by copyright, trademark, and other laws of the United States and foreign countries. Audioread™ is a trademark of Audioread by Astrophe, LLC.</p>

        <h2>9. DMCA Policy</h2>
        <p>We respect intellectual property rights and expect our users to do the same. If you believe that material available on our Service infringes on your copyright, please notify our designated copyright agent at ryan@audioread.com. For detailed information about our DMCA procedures and policies, please see our full <Link to="/dmca">DMCA Policy</Link>.</p>

        <h2>10. RSS Feed Subscription and Copyright Compliance</h2>
        <p>Audioread provides features that allow users to subscribe to RSS feeds for audio conversion and podcast synchronization. By subscribing to any RSS feed through our Service, you explicitly confirm and represent that:</p>
        <ul>
          <li>You have the legal right to access and consume the content of that feed</li>
          <li>Your subscription and consumption of the feed's content does not infringe upon any copyright, intellectual property rights, or other rights of any third party</li>
          <li>You have obtained any necessary permissions or subscriptions required by the content publisher to access the feed</li>
          <li>You will not distribute, share, or republish converted audio content from feeds in violation of applicable copyright laws</li>
        </ul>
        <p>Audioread reserves the right to remove access to any feed or terminate accounts that we determine, in our sole discretion, may create legal liability for copyright or intellectual property infringement. We are not responsible for verifying your rights to access any particular feed, and the availability of a feed through our Service does not constitute an endorsement or verification of your rights to that content.</p>

        <h2>11. Cookie Policy</h2>
        <p>Our Service uses cookies and similar technologies. You can control cookies through your browser settings and other tools. We use the following types of cookies:</p>
        <ul>
          <li>Essential cookies: Required for basic site functionality</li>
          <li>Analytics cookies: Help us understand site usage</li>
          <li>Preference cookies: Remember your settings and choices</li>
          <li>Marketing cookies: Used for targeted advertising (if applicable)</li>
        </ul>
        <p>You can manage your cookie preferences at any time through our <button 
          onClick={() => window.openCookieManager?.()} 
          style={{ 
            background: 'none', 
            border: 'none', 
            color: '#007bff', 
            padding: 0, 
            cursor: 'pointer', 
            textDecoration: 'underline' 
          }}
        >Cookie Management Tool</button>.</p>

        <h2>12. EU-Specific Terms</h2>
        <p>For users in the European Economic Area (EEA):</p>
        <ul>
          <li>You have the right to lodge a complaint with your local data protection authority</li>
          <li>You have the right to data portability</li>
          <li>You have the right to withdraw consent for data processing</li>
          <li>You have the right to object to automated decision-making</li>
          <li>The legal basis for processing your data is outlined in our Privacy Policy</li>
        </ul>

        <h2>13. Limitation of Liability</h2>
        <p>The Service is provided "as is" without warranties of any kind. The Company is not liable for any damages arising from your use of the Service. For EU customers, nothing in these terms limits consumer protection rights that cannot be waived under applicable law.</p>

        <h2>14. Changes to Terms</h2>
        <p>We may modify these terms at any time. Continued use of the Service constitutes acceptance of modified terms. EU users will be notified of material changes as required by applicable law.</p>

        <h2>15. Termination</h2>
        <p>We may terminate or suspend your account at any time for violations of these terms.</p>

        <h2>16. Governing Law</h2>
        <p>These terms are governed by the laws of the State of Delaware, United States, without regard to its conflict of law principles. For EU customers, this does not affect your rights under mandatory local consumer protection laws.</p>

        <h2>Contact</h2>
        <p>For questions about these Terms, please contact:</p>
        <p>
          Audioread by Astrophe, LLC<br />
          8 The Green, Suite 300<br />
          Dover, DE 19901<br />
          Email: ryan@audioread.com<br />
          Phone: +1 (951) 666-3443
        </p>

        <p>EU Representative:<br />
          Ryan Walter<br />
          Email: ryan@audioread.com
        </p>
      </div>
    </div>
  );
};

export default TermsOfService; 